import { Component, ElementRef, NgZone, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { ProjectService } from '../shared/project/project.service';
import { CardsModel, IHasStory, Tab, TabAddress, ViewMode } from '../shared/project/cards/cards-model';
import { WaterfallItem } from './waterfall-item';
import { Card } from '../shared/project/cards/card';
import { DriverTypeFilter, DriverTypeFilterEditor } from '../shared/project/driver-types/driver-type-filter';
import { DriverType } from '../shared/project/driver-types/driver-type';
import { DataService } from '../shared/network/data.service';
import { NotificationService } from '../shared/notificaton.service';
import { PreaggregationStrategy } from '../shared/preaggregation/preaggregation-strategy';
import { DimensionFilterItem, DimensionsFilter, LogicalOperationType } from '../shared/project/dimensions-filtering/dimensions-filter';
import { DimensionName } from '../shared/project/cards/dimension-name';
import { LegendItem } from '../shared/project/cards/legend-item';
import { DisplayService } from '../shared/display-service';
import { DimensionValue } from '../shared/project/cards/dimension-value';
import { BalanceCard } from '../shared/project/cards/balance-card';
import { DriverSelectionAlgorithm } from '../shared/project/cards/driver-selection-algorithm';
import { DriverTypeWithIndex } from '../shared/project/driver-types/driver-type-with-index';
import { ImportProgressService } from '../shared/import/import-progress.service';
import { ImportStep } from '../shared/import/import-step';
import { DriverTypeSelectionItem } from '../shared/project/driver-types/driver-type-selection-item';
import { Quickview } from '../shared/project/cards/quickview';
import { SidebarView } from './sidebar-view';
import { RefVolumeBasis } from '../import/column-types/reference/reference-settings';
import { SignalRService } from '../shared/network/signalr.service';
import { ProcessingProgressDto }  from '../shared/project/processing-progress-dto';
import { DimensionLockType } from '../shared/project/cards/dimension-lock-type';
import { BenchmarkDiagramPoint, BenchmarkDiagramSeries } from '../shared/benchmarks/benchmarks-model';
import { FancyNumberPipe } from '../shared/directives/fancy-number.pipe';
import { CardHelper } from './cardHelper';
import { ViewsModel } from '../shared/project/cards/views-model';
import { CardsMultiSelectMode } from './multiselect-state';
import { GUIUpdateOnDimValSelection } from '../shared/project/cards/gui-update-on-dim-val-selection';
import { CustomLinkModule } from '../shared/quill/quill-custom-link-module';
import { BaseCardsComponent } from './base-cards-component';

@Component({
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss']
})
export class CardsComponent extends BaseCardsComponent implements OnInit, OnDestroy {
    DriverType: typeof DriverType = DriverType;   //hack for enum to be available in the template
    SidebarView: typeof SidebarView = SidebarView;
    ViewMode: typeof ViewMode = ViewMode;
    RefVolumeBasis: typeof RefVolumeBasis = RefVolumeBasis;
    DriverSelectionAlgorithm: typeof DriverSelectionAlgorithm = DriverSelectionAlgorithm;
    PreaggregationStrategy: typeof PreaggregationStrategy = PreaggregationStrategy;
    LogicalOperationType: typeof LogicalOperationType = LogicalOperationType;
    DimensionLockType: typeof DimensionLockType = DimensionLockType;
    CardsMultiSelectMode: typeof CardsMultiSelectMode = CardsMultiSelectMode;
    private driverTypeFilterEditor: DriverTypeFilterEditor;

    public projectId: string;
    viewsModel: ViewsModel;
    cardsModel: CardsModel;
    copiedTab: TabAddress = undefined;
    selectedTabIsCompatible: boolean = false;
    public showFcst: boolean = false;

    driverTypeFilter: DriverTypeFilter;
    public toSeqBeforeOutOfScope: number = undefined;
    public toTotBeforeOutOfScope: number = undefined;
    public toSeqAfterOutOfScope: number = undefined;
    public toTotAfterOutOfScope: number = undefined;
    outComeAfterOutOfScope: number;
    legend: LegendItem[][];
    tabDimensionsFilter: DimensionValue[][];
    tabDriverTypeName: string;
    tabDimensionsList: DimensionValue[];
    scopeDriverTypeNames: string[];

    selectedAlgorithm: DriverSelectionAlgorithm;
    minDriverValue: number = 0;
    savedAlgorithm: DriverSelectionAlgorithm;
    savedMinDriverValue: number;
    showAlgoSelection: boolean = false;

    loading: boolean = true;
    loadingBackground: boolean = false;
    loadingBackgroundPercent: number = 0;
    loadingBackgroundCaption: string = 'Processing data';
    aborted: boolean = false;
    error: string = null;
    retryTimeoutId: any;

    sidebarView: SidebarView = SidebarView.Hide;
    driverTypeFilterIsDirty = false;
    showAllViews = true;
    multiSelectMode: CardsMultiSelectMode = CardsMultiSelectMode.None;

    dimensionsFilterEditMode = false;
    selectingDimensionForItemIndexed: number = undefined;
    selectingValueForItemIndexed: number = undefined;
    selectedDimensionName: DimensionName;
    private dimensionsUniqueValuesCache: {};
    loadingDimensionsUniqueValuesFor: number[] = [];
    dimensionValuesFilter = '';
    filteredDimensionValues: string[] = [];
    dimensionsFilterIsDirty = false;
    savedDimensionsFilter: DimensionsFilter;
    showingDimFilterSaveWarn = false;
    selectingDimValueForTabDimIndex : number = undefined;

    depthFiltersDirty = false;
    showDepthSlider = false;
    allowedMinDepth = 0;
    allowedMaxDepth = 9;
    selectedMinDepth = 0;
    selectedMaxDepth = 9;
    maxPossibleDepth = 9;
    savedMinDepth: number;
    savedMaxDepth: number;

    showNavigator: boolean = false;

    storiesAreBeingLoaded: boolean = false;
    story: string;
    savedStory: string;
    loadingStory: boolean = false;

    previewFormCompVarPercCtx = {
        showPlusForPositive: true,
        roundTo: 1,
        use2stepRounding: false,
    };
    previewOutcomeVarPercCtx = {
        cap: 999,
        showPlusForPositive: true,
        roundTo: 1,
        use2stepRounding: false,
    };
    quickViewDeltasCtx = {
        roundTo: 1,
        showPlusForPositive: true,
        cap: undefined,
        scale: undefined,
    };

    @ViewChildren('tabsContainer') tabContainersNativeElements: QueryList<ElementRef>;

    //diagrams in cards
    public pointWidth = 50;
    public viewBoxWidth = 100;
    public viewBoxHeight = 40;

    public xlsxDownloadLink: string = '';

    public quillModules = {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
          //['blockquote', 'code-block'],

          [{ 'align': [] }],
          //[{ 'header': 1 }, { 'header': 2 }],               // custom button values
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
          [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent

          [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

          [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
          //[{ 'font': [] }],

          ['clean'],                                         // remove formatting button

          ['link'/*, 'image', 'video'*/]                         // link and image, video
        ],
        customLink: new CustomLinkModule(),
      };

    constructor(private activatedRoute: ActivatedRoute, private projectService: ProjectService, private router: Router,
        private dataService: DataService, private notifier: NotificationService, private displayService: DisplayService,
        private ngZone: NgZone, private importProgressService: ImportProgressService, private signalRService: SignalRService) {
            super();
            this.driverTypeFilterEditor = new DriverTypeFilterEditor();
            this.applyDimensionValuesFilter = this.debounce(() => this.applyDimensionValuesFilterInternal());
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            let projectId = queryParams.projectId;
            let tabUId = queryParams.tabuid;
            if (projectId) {
                if (this.projectId == projectId && tabUId !== undefined) {
                    this.selectTabByUId(tabUId);
                } else {
                    this.projectId = projectId;
                    this.signalRService.startConnection();
                    this.signalRService.addInitialProcessingProgressListener(this.createOnProgressUpdateHandler());
                    this.projectService.getViews(projectId).then(res => {
                        this.loadViews(res, tabUId);
                        if (history.state.returnViewId) {
                            this.selectTabById(history.state.returnViewId, history.state.returnTabId);
                        } else {
                            let savedSelectedViewId = this.getSavedSelectedViewId();
                            if (savedSelectedViewId && this.viewsModel) {
                                let view = this.viewsModel.views.find(v => v.viewId == savedSelectedViewId);
                                if (view) {
                                    this.loadModel(view);
                                }
                            }
                        }
                        this.importProgressService.increaseImportStep(ImportStep.cards);
                    });
                }
            }
        });

    }

    private maxWidth = 0;
    ngAfterViewInit() {
        /*this.tabContainersNativeElements.changes.subscribe(() => {
            let maxW = 0;
            this.tabContainersNativeElements.forEach(el => {
                console.log(el.nativeElement.offsetWidth);
                console.log(el.nativeElement.width);
                console.log(el.nativeElement.scrollWidth);
            });
        });*/
    }

    ngOnDestroy(): void {
        this.signalRService.stopConnection();
    }

    public scrollFixStyle = null;
    private applyFixWidthForScroll() {
        let maxW = 0;
        this.tabContainersNativeElements.forEach(el => {
            if (maxW < el.nativeElement.scrollWidth) {
                maxW = el.nativeElement.scrollWidth;
            }
        });
        this.scrollFixStyle = 'width:' + maxW + 'px';
        this.tabContainersNativeElements.forEach(el => {
            el.nativeElement.style.width = maxW;
        });
    }
    private removeFixWidthForScroll() {
        this.scrollFixStyle = null;
    }
    private reapplyFixWidthForScroll() {
        this.removeFixWidthForScroll();
        setTimeout(() => {
            this.applyFixWidthForScroll();
        }, 1);
    }

    private createXlsxDownLoadLink() {
        if (!this.cardsModel) {
            return;
        }
        let link = `${this.dataService.getBaseUrl()}/Processing/DownloadXlsx?viewId=${this.cardsModel.viewId}`;
        if (this.cardsModel.selectedTabId && !this.showFcst) {
            link += `&tabId=${this.cardsModel.selectedTabId}`;
        }
        this.xlsxDownloadLink = link;
    }

    private cloneObject<T>(obj: T):T {
        return JSON.parse(JSON.stringify(obj));
    }

    private retryCounter: number = 0;
    private initRetry() {
        this.loadingBackground = true;
        this.aborted = false;
        this.error = null;
        this.retryCounter++;
        let self = this;
        this.stopRetries();
        this.retryTimeoutId = setTimeout(()=>{
            console.log('Retrying... Try #' + self.retryCounter);
            self.loadCardsFromServer();
        }, self.getRetryTime());
    }

    private getRetryTime() {
        if (this.retryCounter <= 1) {
            return 1500;
        } else {
            return (6 + this.retryCounter) * 1000;
        }
    }

    private loadCardsFromServer() {
        this.stopRetries();
        this.projectService.getViews(this.projectId).then(res => {
            this.loadViews(res, undefined);
            this.createXlsxDownLoadLink();
        });
    }

    private loadViews(viewsModel: ViewsModel, tabUId: string): void {
        if (viewsModel.processingInProgress) {
            this.loadingBackground = true;
            this.loadProcessingProgress(viewsModel.currentProcessingProgess, this);
            this.initRetry();
            this.loading = false;
            return;
        } else {
            this.loadingBackground = false;
            this.retryCounter = 0;
        }
        this.viewsModel = viewsModel;
        if (viewsModel.storiesAreBeingLoaded) {
            this.storiesAreBeingLoaded = true;
            this.loadStories();
        }
        for (let view of viewsModel.views) {
            this.initModel(view);
        }
        let selectedView: CardsModel;
        if (tabUId !== undefined) {
            let view = this.findViewByTabUId(tabUId);
            if (view) {
                this.selectView(view);
            }
        }
        this.sortViewsBySelection();
        selectedView = this.viewsModel.views[this.viewsModel.views.length - 1];
        this.loadModel(selectedView, tabUId);
    }

    private loadModel(model: CardsModel, tabUId: string = undefined): void {
        let selectedViewIx = this.viewsModel.views.findIndex(v => v.viewId == model.viewId);
        this.viewsModel.views[selectedViewIx] = model;
        this.copiedTab = undefined;

        this.initModel(model);

        this.cardsModel = model;
        this.savedDimensionsFilter = JSON.parse(JSON.stringify(model.dimensionsFilter));

        if (tabUId != undefined)  {
            this.selectTabByUId(tabUId);
        } else {
            if (!model.selectedTabId) {
                this.selectMainTab(this.cardsModel);
            } else {
                this.selectTab(this.cardsModel, this.getSelectedTab());
            }
        }
        if (this.activatedRoute.snapshot.queryParams['tabuid'] != tabUId) {
            this.routeToTabUId(tabUId);
        }

        this.dimensionsUniqueValuesCache = {};
        this.loadingDimensionsUniqueValuesFor = [];
        this.dimensionsFilterEditMode = false;
        this.selectingDimensionForItemIndexed = undefined;
        this.selectingValueForItemIndexed = undefined;
        this.driverTypeFilterIsDirty = false;
        this.dimensionsFilterIsDirty = false;
        this.depthFiltersDirty = false;
        this.showDepthSlider = false;
        this.showingDimFilterSaveWarn = false;
        this.loading = false;
        this.turnOffMultiSelect();
        if (model.warningText) {
            this.notifier.showInfo(model.warningText);
        }
        let self = this;
        setTimeout(() => {
            self.applyFixWidthForScroll();
        }, 1);
    }

    private initModel(model: CardsModel): void {
        //select and init diagram series
        for (let i = 0; i < model.variance.cards.length; ++i) {
            if (model.variance.cards[i].altViewSeq.diagramSeries && model.variance.cards[i].altViewSeq.diagramSeries.length) {
                CardHelper.initCardDiagram(model.variance.cards[i], model.selectedDiagramSeriesName, model.variance.oldControlValue, model.variance.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
            }
            if (model.forecast && model.forecast.cards[i].altViewSeq.diagramSeries && model.forecast.cards[i].altViewSeq.diagramSeries.length) {
                CardHelper.initCardDiagram(model.forecast.cards[i], model.selectedDiagramSeriesName, model.forecast.oldControlValue, model.forecast.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
            }
            for (let j = 0; j < model.tabs.length; ++j) {
                for (let i = 0; i < model.tabs[j].variance.cards.length; ++i) {
                    if (model.tabs[j].variance.cards[i].altViewSeq.diagramSeries && model.tabs[j].variance.cards[i].altViewSeq.diagramSeries.length) {
                        CardHelper.initCardDiagram(model.tabs[j].variance.cards[i], model.selectedDiagramSeriesName, model.variance.oldControlValue, model.variance.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
                    }
                    if (model.tabs[j].forecast && model.tabs[j].forecast.cards[i].altViewSeq.diagramSeries && model.tabs[j].forecast.cards[i].altViewSeq.diagramSeries.length) {
                        CardHelper.initCardDiagram(model.tabs[j].forecast.cards[i], model.selectedDiagramSeriesName, model.forecast.oldControlValue, model.forecast.newControlValue, this.seqMode, this.viewBoxWidth, this.viewBoxHeight);
                    }
                }
            }
        }

        this.loadBgColorToDimensionsFilter(model.legend, model.dimensionsFilter);
    }

    private loadMainTab(model: CardsModel) {
        this.varianceModel = this.showFcst ? model.forecast : model.variance;
        this.loadWaterfallItems(this.varianceModel.cards, this.varianceModel.outOfScopeCards, this.varianceModel.balanceCard);
        this.driverTypeFilter = this.cloneObject(model.driverTypeFilter);
        this.driverTypeFilterEditor.setManuallySelected(this.driverTypeFilter);
        this.toSeqBeforeOutOfScope = this.toTotBeforeOutOfScope = this.toTotAfterOutOfScope = undefined;
        this.legend = this.cloneObject(model.legend);
        this.maxPossibleDepth = model.legend.length;
        this.savedMinDepth = this.selectedMinDepth = model.minDriverDepth || 0;
        this.savedMaxDepth = this.selectedMaxDepth = model.maxDriverDepth === 0 ? 0 : (model.maxDriverDepth || this.maxPossibleDepth);
        this.tabDimensionsFilter = null;
        this.selectedAlgorithm = this.savedAlgorithm = model.algorithm;
        this.minDriverValue = this.savedMinDriverValue = model.minDriverValue;
        this.showAlgoSelection = false;
        this.recalcDepthSliderConstraints();
        this.scopeDriverTypeNames = model.scopeDriverTypeNames;
        this.createXlsxDownLoadLink();
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        this.calcOutcomeCtxs();
    }

    private selectMainTab(view: CardsModel) {
        this.turnOffMultiSelect();
        this.selectView(view);
        view.selectedTabId = undefined;
        this.selectedTabIsCompatible = view.mainTabIsMarkedCompatible;
        this.driverTypeFilterIsDirty = false;
        this.depthFiltersDirty = false;
        this.dimensionsFilterEditMode = false;
        this.showDepthSlider = false;
        this.tabDimensionsList = undefined;
        this.loadMainTab(this.cardsModel);
        this.cancelDimensionValueSelection();
        this.savedStory = this.story = view.story;
        this.loadingStory = view.loadingStory;

        this.getAndLogRecommendedBreakDown();
    }

    private selectTab(view: CardsModel, tab: Tab) {
        this.turnOffMultiSelect();
        this.selectView(view);
        view.selectedTabId = tab.id;
        this.selectedTabIsCompatible = tab.isMarkedCompatible;
        this.varianceModel = this.showFcst ? tab.forecast : tab.variance;
        this.loadWaterfallItems(this.varianceModel.cards, this.varianceModel.outOfScopeCards, this.varianceModel.balanceCard);
        this.driverTypeFilter = this.createDriverTypeFilterWithSingleVal(this.cardsModel.driverTypeFilter, tab.driverType, tab.formulaComponentIndex, tab.availableDriverTypes, tab.selectedViewDriverTypes);

        this.toSeqBeforeOutOfScope = tab.toSeqBeforeOutOfScope;
        this.toTotBeforeOutOfScope = tab.toTotBeforeOutOfScope;
        this.toSeqAfterOutOfScope = tab.toSeqAfterOutOfScope;
        this.toTotAfterOutOfScope = tab.toTotAfterOutOfScope;
        this.outComeAfterOutOfScope = tab.outComeAfterOutOfScope;

        this.legend = this.cloneObject(tab.legend);
        this.maxPossibleDepth = tab.legend.length;
        this.savedMinDepth = this.selectedMinDepth = tab.minDriverDepth || 0;
        this.savedMaxDepth = this.selectedMaxDepth = tab.maxDriverDepth === 0 ? 0 : (tab.maxDriverDepth || this.maxPossibleDepth);
        this.tabDimensionsFilter = this.cloneObject(tab.dimensionValues);
        this.tabDimensionsList = tab.displayDimensionValues;
        this.tabDriverTypeName = tab.driverTypeName;
        this.driverTypeFilterIsDirty = false;
        this.depthFiltersDirty = false;
        this.dimensionsFilterEditMode = false;
        this.showDepthSlider = false;
        this.selectedAlgorithm = this.savedAlgorithm = tab.algorithm;
        this.minDriverValue = this.savedMinDriverValue = tab.minDriverValue;
        this.showAlgoSelection = false;
        this.cancelDimensionValueSelection();
        this.recalcDepthSliderConstraints();
        this.scopeDriverTypeNames = tab.scopeDriverTypeNames;
        this.createXlsxDownLoadLink();
        CardHelper.calcCardsFancyNumContexts(this.varianceModel.cards);
        this.calcOutcomeCtxs();
        this.savedStory = this.story = tab.story;
        this.loadingStory = tab.loadingStory;

        this.getAndLogRecommendedBreakDown();
    }

    private getAndLogRecommendedBreakDown() {
        //temp method for debugging
        const startTime = new Date().getTime();
        this.dataService.get<number[]>(`processing/findRecommendedBreakdownForCard?viewId=${this.cardsModel.viewId}&tabId=${this.cardsModel.selectedTabId}&cardIx=${1}`)
        .then(res => {
            const endTime = new Date().getTime();
            console.log(`recommended breakdown for 2nd card  (took ${endTime - startTime} ms.):`);
            console.log(res);
        });
    }

    public mainTabClick(view: CardsModel) {
        if (view.tabUId) {
            this.routeToTabUId(view.tabUId);
        } else {
            this.selectMainTab(view);
        }
        this.saveSelectedViewId(view.viewId);
    }

    public tabClick(view: CardsModel, tab: Tab) {
        if (tab.tabUId) {
            this.routeToTabUId(tab.tabUId);
        } else {
            this.selectTab(view, tab);
        }
        this.saveSelectedViewId(view.viewId);
    }

    private routeToTabUId(tabUId: string) {
        this.router.navigate(
            [],
            {
              relativeTo: this.activatedRoute,
              queryParams: { tabuid: tabUId },
              queryParamsHandling: 'merge'
            });
    }

    private selectTabByUId(tabUId: string) {
        let view = this.viewsModel.views.find(v => v.tabUId === tabUId || (v.tabs && v.tabs.some(t => t.tabUId == tabUId)));
        if (view.tabUId == tabUId) {
            this.selectMainTab(view);
        } else {
            let noTab = false;
            if (!view.tabs) {
                noTab = true;
                this.notifier.showError("No such tab");
            } else {
                let tab = view.tabs.find(t => t.tabUId == tabUId);
                if (!tab) {
                    noTab = true;
                    this.notifier.showError("No such tab");
                } else {
                    this.selectTab(view, tab);
                }
            }
            if (noTab) {
                this.selectMainTab(view);
            }
        }
    }

    public selectTabById(viewId: string, tabId: string) {
        let view: CardsModel;
        for (let v of this.viewsModel.views) {
            if (v.viewId == viewId) {
                view = v;
                break;
            }
        }
        if (!view) {
            return;
        }
        if (tabId) {
            for (let t of view.tabs) {
                if (t.id == tabId) {
                    this.selectTab(view, t);
                    break;
                }
            }
        } else {
            this.selectMainTab(view);
        }
    }

    public selectForecastWalk(view: CardsModel) {
        this.selectView(view);
        if (!this.cardsModel.forecastWalk) {
            return;
        }
        this.cardsModel.selectedTabId = 'fw';
        this.showAlgoSelection = false;
        this.loadWaterfallItems(this.cardsModel.forecastWalk.cards, [], undefined);
        this.calcOutcomeCtxs();
        this.createXlsxDownLoadLink();
        this.router.navigate(
            [],
            {
              relativeTo: this.activatedRoute,
              queryParams: { tabuid: 'fw' },
              queryParamsHandling: 'merge'
            });
    }

    private loadBgColorToDimensionsFilter(legend: DimensionName[][], dimensionsFilter: DimensionsFilter) {
        try {
            if(dimensionsFilter && dimensionsFilter.items) {
                for (let i = 0; i < dimensionsFilter.items.length; ++i) {
                    dimensionsFilter.items[i].bgColor = this.findBgColorFromLegend(legend, dimensionsFilter.items[i].dimensionIndex);
                }
            }
        } catch (ex) {
            console.error(ex);
        }
    }

    private findBgColorFromLegend(legend: DimensionName[][], dimIndex: number): string {
        let dim = this.findDimensionInLegend(legend, dimIndex);
        if(dim) {
            return dim.bgColor;
        }
        return '#000000';
    }

    private findDimensionInLegend(legend: DimensionName[][], dimIndex: number): DimensionName {
        for (let i = 0; i < legend.length; ++i) {
            for (let j = 0; j < legend[i].length; ++j) {
                if(legend[i][j].index == dimIndex) {
                    return legend[i][j];
                }
            }
        }
        return undefined;
    }

    dragStarted(event: any) {
        this.showWaterfall = false;
    }

    async dropped(event: any) {
        this.showWaterfall = true;
        if (event.previousIndex == event.currentIndex) {
            return;
        }
        this.loading = true;
        try {
            let res: any = await this.projectService.reorderCards(this.cardsModel.viewId, this.cardsModel.selectedTabId, event.previousIndex, event.currentIndex);
            this.loadModel(res);
        } finally {
            this.loading = false;
        }
    }

    async toggleLocked(card: Card) {
        this.updateUIWithoutModelReload();
        card.isLocked = !card.isLocked;
        if(!card.isLocked) {
            this.loading = true;
        }
        let cardIndex = this.varianceModel.cards.indexOf(card);
        let res: any = await this.projectService.setCardLock(this.cardsModel.viewId, this.cardsModel.selectedTabId, cardIndex, card.isLocked);
        if(!card.isLocked) {
            try {
                this.loadModel(res);
            } finally {
                this.loading = false;
            }
        }
    }

    private getSelectedTab() {
        return this.cardsModel.tabs.find(t => t.id === this.cardsModel.selectedTabId);
    }

    public setSeqMode(val: boolean): void {
        this.seqMode = val;
        this.loadWaterfallItems(this.varianceModel.cards, this.varianceModel.outOfScopeCards, this.varianceModel.balanceCard);
        for (let card of this.varianceModel.cards) {
            let name = card.selectedDiagram.name;
            card.selectedDiagram = (val ? card.altViewSeq.diagramSeries : card.altViewTot.diagramSeries).find(s => s.name == name);
        }
    }

    setNextDriverTypeFilterViewVal(item: DriverTypeSelectionItem): void {
        this.driverTypeFilterIsDirty = true;
        this.driverTypeFilterEditor.setNextViewVal(this.driverTypeFilter, item);
    }

    selectNextDriverTypeScopeVal(driverType: DriverType, formulaComponentIndex? : number): void {
        this.driverTypeFilterIsDirty = true;
        if (this.cardsModel.selectedTabId) {
            this.driverTypeFilterEditor.selectSingleScope(this.driverTypeFilter, driverType, formulaComponentIndex);
        } else {
            this.driverTypeFilterEditor.setNextScopeVal(this.driverTypeFilter, driverType, formulaComponentIndex);
        }
    }


    selectTabDriverType(driverType: DriverType, formulaComponentIndex? : number): void {
        if (!this.cardsModel.selectedTabId) {
            return;
        }
        let tab = this.cardsModel.tabs.find(t => t.id == this.cardsModel.selectedTabId);
        if (!tab.availableDriverTypes.find(dtwi => dtwi.type == driverType && dtwi.formulaComponentIndex == formulaComponentIndex)) {
            return;
        }
        this.driverTypeFilterIsDirty = true;
        this.driverTypeFilterEditor.selectSingleScope(this.driverTypeFilter, driverType, formulaComponentIndex);
    }

    private createDriverTypeFilterWithSingleVal(filter: DriverTypeFilter, selectedType: DriverType, formulaComponentIndex: number, availableDriverTypes: DriverTypeWithIndex[], selectedViewDriverTypes: DriverTypeWithIndex[]) : DriverTypeFilter {
        let newFilter = this.cloneObject(filter);
        this.driverTypeFilterEditor.selectSingleScope(newFilter, selectedType, formulaComponentIndex);
        this.driverTypeFilterEditor.selectViewTypes(newFilter, selectedViewDriverTypes);
        let allItems = this.driverTypeFilterEditor.getAllItems(newFilter);
        for (let i = 0; i < allItems.length; ++i) {
            let item = allItems[i];
            if (!availableDriverTypes.find(t => t.type == item.driverTypeWithIndex.type && t.formulaComponentIndex == item.driverTypeWithIndex.formulaComponentIndex)) {
                item.disabled = true;
            }
        }
        return newFilter;
    }

    async applyDriverTypeFilter() {
        if(this.driverTypeFilterEditor.allScopeUnselected(this.driverTypeFilter)) {
            this.notifier.showError('Please select at least one driver type');
            return;
        }

        this.driverTypeFilterIsDirty = false;
        if(this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.projectService.applyDriverTypeFilter(this.cardsModel.viewId, this.cardsModel.selectedTabId, this.driverTypeFilter);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    async cancelDriverTypeFilterChanges() {
        this.driverTypeFilterIsDirty = false;
        if (this.cardsModel.selectedTabId) {
            let tab = this.cardsModel.tabs.find(t => t.id == this.cardsModel.selectedTabId);
            this.driverTypeFilter = this.createDriverTypeFilterWithSingleVal(this.cardsModel.driverTypeFilter, tab.driverType, tab.formulaComponentIndex, tab.availableDriverTypes, tab.selectedViewDriverTypes);
        } else {
            this.driverTypeFilter = this.cloneObject(this.cardsModel.driverTypeFilter);
        }
    }

    cancelDimensionsFilterChanges() {
        if (this.cardsModel.selectedTabId) {
            let tab = this.cardsModel.tabs.find(t => t.id == this.cardsModel.selectedTabId);
            this.tabDimensionsFilter = this.cloneObject(tab.dimensionValues);
        } else {
            if (!this.savedDimensionsFilter) {
                this.cardsModel.dimensionsFilter = null;
            } else {
                this.cardsModel.dimensionsFilter = JSON.parse(JSON.stringify(this.savedDimensionsFilter));
            }
        }
        this.dimensionsFilterIsDirty = false;
    }

    cancelDimensionSelection() {
        this.selectingDimensionForItemIndexed = undefined;
        this.selectingDimValueForTabDimIndex = undefined;
    }

    public setTabDimValToIsAll() {
        let dimIndex = this.selectingDimValueForTabDimIndex;
        for (let i = 0; i < this.tabDimensionsFilter.length; ++i) {
            for (let j = 0; j < this.tabDimensionsFilter[i].length; ++j) {
                let dimVal = this.tabDimensionsFilter[i][j];
                if(dimVal.index == dimIndex) {
                    dimVal.value = null;
                    dimVal.isAll = true;
                }
            }
        }
        this.dimensionsFilterIsDirty = true;
        this.selectingDimValueForTabDimIndex = undefined;
    }

    async undo() {
        if (this.loading || (this.cardsModel && !this.cardsModel.undoAvailable)) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.projectService.undo(this.cardsModel.viewId);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    async redo() {
        if (this.loading || !this.cardsModel.redoAvailable) {
            return;
        }
        try {
            this.loading = true;
            var model = await this.projectService.redo(this.cardsModel.viewId);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    enableDimensionSelectUI(i: number) {
        this.selectingDimensionForItemIndexed = i;
    }

    enableDimensionValueSelectUI(i: number) {
        this.selectingValueForItemIndexed = i;
        this.dimensionValuesFilter = this.cardsModel.dimensionsFilter.items[i].value;
        this.loadUniqueValuesForDimension(this.cardsModel.dimensionsFilter.items[i].dimensionIndex);
        this.applyDimensionValuesFilterInternal();
        this.displayService.scrollToClass(this.ngZone, 'search-results', 500);
    }

    editItemVal(i: number): void {
        if(this.cardsModel.dimensionsFilter.items[i].dimensionIndex == undefined) {
            this.enableDimensionSelectUI(i);
        } else {
            this.selectedDimensionName = this.findDimensionInLegend(this.cardsModel.legend, this.cardsModel.dimensionsFilter.items[i].dimensionIndex);
            this.enableDimensionValueSelectUI(i);
        }
    }

    selectDimension(dimension: DimensionName) {
        this.selectedDimensionName = dimension;
        let dimFilterItem = this.cardsModel.dimensionsFilter.items[this.selectingDimensionForItemIndexed];
        dimFilterItem.dimensionIndex = dimension.index;
        dimFilterItem.bgColor = dimension.bgColor;
        dimFilterItem.fontColor = dimension.fontColor;
        let i = this.selectingDimensionForItemIndexed;
        this.selectingDimensionForItemIndexed = undefined;
        this.enableDimensionValueSelectUI(i);
    }

    addNewDimensionFilterItem(index: number) {
        this.ensureDimensionsFilterIsValid();
        let newItem = new DimensionFilterItem();
        newItem.openBracketsCount = 0;
        newItem.closeBracketsCount = 0;
        newItem.not = false;
        newItem.bgColor = '#fde5d7';
        newItem.fontColor = '#0070c1';
        if (this.cardsModel.dimensionsFilter.items) {
            this.cardsModel.dimensionsFilter.items.splice(index, 0, newItem);
        } else {
            this.cardsModel.dimensionsFilter.items = [ newItem ];
        }
        this.dimensionsFilterEditMode = true;
    }

    private ensureDimensionsFilterIsValid() {
        if (!this.cardsModel.dimensionsFilter) {
            this.cardsModel.dimensionsFilter = new DimensionsFilter();
            this.cardsModel.dimensionsFilter.items = [];
        }
    }

    removeDimensionsFilterItem(index: number): void {
        this.cardsModel.dimensionsFilter.items.splice(index, 1);
        this.dimensionsFilterIsDirty = true;
    }

    toggleAnd(item: DimensionFilterItem): void {
        if (item.operationType === LogicalOperationType.And) {
            item.operationType = null;
        } else {
            item.operationType = LogicalOperationType.And;
        }
        this.dimensionsFilterIsDirty = true;
    }

    toggleOr(item: DimensionFilterItem): void {
        if (item.operationType === LogicalOperationType.Or) {
            item.operationType = null;
        } else {
            item.operationType = LogicalOperationType.Or;
        }
        this.dimensionsFilterIsDirty = true;
    }

    addOpenBracket(item: DimensionFilterItem): void {
        ++item.openBracketsCount;
        this.dimensionsFilterIsDirty = true;
    }

    addCloseBracket(item: DimensionFilterItem): void {
        ++item.closeBracketsCount;
        this.dimensionsFilterIsDirty = true;
    }

    removeOpenBracket(item: DimensionFilterItem): void {
        if (item.openBracketsCount > 0){
            --item.openBracketsCount;
        }
        this.dimensionsFilterIsDirty = true;
    }

    removeCloseBracket(item: DimensionFilterItem): void {
        if (item.closeBracketsCount > 0){
            --item.closeBracketsCount;
        }
        this.dimensionsFilterIsDirty = true;
    }

    private applyDimensionValuesFilterInternal() {
        let dimensionIndex = this.cardsModel.selectedTabId ? this.selectingDimValueForTabDimIndex : this.cardsModel.dimensionsFilter.items[this.selectingValueForItemIndexed].dimensionIndex;
        this.dimensionsUniqueValuesCache = this.dimensionsUniqueValuesCache || {};
        let cachedVals = this.dimensionsUniqueValuesCache[dimensionIndex];
        if (!cachedVals) {
            return;
        }
        if(!this.dimensionValuesFilter) {
            this.filteredDimensionValues = cachedVals;//.slice(0, 5);
            return;
        }
        this.filteredDimensionValues = cachedVals.filter(s => new RegExp(this.dimensionValuesFilter, 'i').test(s));
    }

    private async loadUniqueValuesForDimension(index : number) {
        if(this.loadingDimensionsUniqueValuesFor.indexOf(index) >= 0) {
            return;
        }
        this.loadingDimensionsUniqueValuesFor.push(index);
        let vals = await this.dataService.get<string[]>(`processing/getUniqueValuesForDimension?projectId=${this.projectId}&dimensionIndex=${index}`);
        this.dimensionsUniqueValuesCache[index] = vals;
        let ix = this.loadingDimensionsUniqueValuesFor.indexOf(index);
        if (ix > -1) {
            this.loadingDimensionsUniqueValuesFor.splice(index, 1);
        }
        this.applyDimensionValuesFilterInternal();
    }

    cancelDimensionValueSelection() {
        let dimIndex = this.selectingDimValueForTabDimIndex;
        if (dimIndex != undefined) {
            this.selectingDimValueForTabDimIndex = undefined;
        } else {
            if(this.selectingValueForItemIndexed) {
                let selectedItem = this.cardsModel.dimensionsFilter.items[this.selectingValueForItemIndexed];
                if(selectedItem.dimensionIndex != undefined
                    && !selectedItem.value) {
                        selectedItem.dimensionIndex = undefined;
                }
            }
        }
        this.finishDimensionValueSelection();
    }

    finishDimensionValueSelection() {
        this.selectingValueForItemIndexed = undefined;
        this.dimensionValuesFilter = '';
        this.filteredDimensionValues = [];
    }

    async selectDimensionValue (val: string) {
        if (this.cardsModel.selectedTabId) {
            await this.selectDimensionValueForTab(val);
        } else {
            await this.selectDimensionValueForMainView(val);
        }
    }

    async selectDimensionValueForTab (val: string) {
        let dimIndex = this.selectingDimValueForTabDimIndex;
        let selectedItems: DimensionValue[] = [];
        for (let i = 0; i < this.tabDimensionsFilter.length; ++i) {
            for (let j = 0; j < this.tabDimensionsFilter[i].length; ++j) {
                if (this.tabDimensionsFilter[i][j].index == dimIndex) {
                    selectedItems.push(this.tabDimensionsFilter[i][j]);
                }
            }
        }
        if (selectedItems.every(x => x.value == val)) { //the same value was selected
            this.finishDimensionValueSelection();
            return;
        }
        for (let i = 0; i < selectedItems.length; ++i) {
            selectedItems[i].value = val;
            selectedItems[i].isAll = false;
        }
        this.dimensionsFilterIsDirty = true;
        let res = await this.dataService.get<any>(`Processing/GetGUIUpdateWhenSelectingDimValue?projectId=${this.projectId}&dimensionIndex=${this.selectingDimValueForTabDimIndex}&value=${encodeURIComponent(val)}`);

        for (let i = 0; i < selectedItems.length; ++i) {
            selectedItems[i].fontColor = res.fontColor;
        }
        this.updateDimFilter(res.guiUpdate, this.tabDimensionsFilter);

        this.selectingDimValueForTabDimIndex = undefined;
    }

    private updateDimFilter(guiUpd: GUIUpdateOnDimValSelection, dimFilter: DimensionValue[][]): void {
        for (let i = 0; i < dimFilter.length; ++i) {
            for (let j = 0; j < dimFilter[i].length; ++j) {
                let itemToSetToVal = guiUpd.itemsToSetToVal.find(x => x.dimensionIndex == dimFilter[i][j].index);
                if (itemToSetToVal) {
                    dimFilter[i][j].value = itemToSetToVal.value;
                    dimFilter[i][j].fontColor = itemToSetToVal.fontColor;
                }
                if (guiUpd.dimensionsToSetToAll.indexOf(dimFilter[i][j].index) >= 0) {
                    dimFilter[i][j].value = undefined;
                    dimFilter[i][j].isAll = true;
                }
            }
        }
    }

    async selectDimensionValueForMainView (val: string) {
        let selectedItem = this.cardsModel.dimensionsFilter.items[this.selectingValueForItemIndexed];
        selectedItem.value = val;
        this.finishDimensionValueSelection();
        this.dimensionsFilterIsDirty = true;
        let res = await this.dataService.get<any>(`Processing/GetGUIUpdateWhenSelectingDimValue?projectId=${this.projectId}&dimensionIndex=${selectedItem.dimensionIndex}&value=${encodeURIComponent(val)}`);
        selectedItem.fontColor = res.fontColor;
    }

    applyDimensionValuesFilter: () => void;

    private debounce(func: () => void, wait = 100) {
        let h: any;
        return () => {
            clearTimeout(h);
            h = setTimeout(() => func(), wait);
        };
    }

    public async applyDimensionsFilter() {
        this.loading = true;
        try {
            this.showingDimFilterSaveWarn = false;
            let dimFilter = this.cardsModel.selectedTabId ? DimensionsFilter.FromDimensionValues(this.tabDimensionsFilter) : this.cardsModel.dimensionsFilter;
            let cardsModel = await this.projectService.applyDimensionsFilter(this.cardsModel.viewId, this.cardsModel.selectedTabId, dimFilter);
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public setDepthFiltersDirty(val: boolean): void {
        if (!this.depthFiltersDirty && val) {
            this.displayService.scrollToClass(this.ngZone, 'apply-depth-filters-buttons');
        }
        this.depthFiltersDirty = val;
    }

    public setDimLock(dimIndex: number, val: DimensionLockType) {
        this.setDepthFiltersDirty(true);
        switch (val) {
            case DimensionLockType.None:
                for (let i = 0; i < this.legend.length; ++i) {
                    for (let j = 0; j < this.legend[i].length; ++j) {
                        if (this.legend[i][j].index == dimIndex) {
                            this.legend[i][j].lockType = DimensionLockType.None;
                        }
                    }
                }
                break;
            case DimensionLockType.Value:
                for (let i = 0; i < this.legend.length; ++i) {
                    let dimIsInCurHier = false;
                    for (let j = 0; j < this.legend[i].length; ++j) {
                        if (this.legend[i][j].index == dimIndex) {
                            dimIsInCurHier = true;
                            break;
                        }
                    }
                    if (dimIsInCurHier) {
                        for (let j = 0; j < this.legend[i].length; ++j) {
                            if (this.legend[i][j].index == dimIndex) {
                                this.legend[i][j].lockType = DimensionLockType.Value;
                            } else {
                                this.legend[i][j].lockType = DimensionLockType.None;
                            }
                        }
                    }
                }
                break;
            case DimensionLockType.All:
                for (let i = 0; i < this.legend.length; ++i) {
                    let hasSetAValToAllInCurHier = false;
                    for (let j = 0; j < this.legend[i].length; ++j) {
                        if (this.legend[i][j].index == dimIndex) {
                            this.legend[i][j].lockType = DimensionLockType.All;
                            hasSetAValToAllInCurHier = true;
                        } else if (hasSetAValToAllInCurHier) {
                            this.legend[i][j].lockType = DimensionLockType.None;
                        }
                    }
                }
                break;
        }
        this.recalcDepthSliderConstraints();
    }

    cancelDepthFiltersChanges() {
        this.legend = this.cloneObject(this.getSavedCurrentLegend());
        this.setDepthFiltersDirty(false);
        this.selectedMinDepth = this.savedMinDepth;
        this.selectedMaxDepth = this.savedMaxDepth;
        this.showDepthSlider = false;
    }

    private getSavedCurrentLegend(){
        if (this.cardsModel.selectedTabId) {
            return this.cardsModel.tabs.find(t => t.id == this.cardsModel.selectedTabId).legend;
        } else {
            return this.cardsModel.legend;
        }
    }

    async applyDepthFilters() {
        if (this.selectedMinDepth == this.savedMinDepth
            && this.selectedMaxDepth == this.savedMaxDepth
            && JSON.stringify(this.getSavedCurrentLegend()) === JSON.stringify(this.legend)) {
            this.setDepthFiltersDirty(false);
            this.showDepthSlider = false;
            return;
        }

        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.projectService.applyDepthFilters(this.cardsModel.viewId, this.cardsModel.selectedTabId, this.selectedMinDepth, this.selectedMaxDepth, this.getLockedDimensions(this.legend));
            this.setDepthFiltersDirty(false);
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    private getDimensionsCount(legend: LegendItem[][]): number {
        let maxDimIndex = 0;
        for (let i = 0; i < legend.length; ++i) {
            for (let j = 0; j < legend[i].length; ++j) {
                if (maxDimIndex < legend[i][j].index) {
                    maxDimIndex = legend[i][j].index;
                }
            }
        }
        return maxDimIndex + 1;
    }

    private getLockedDimensions(legend: LegendItem[][]): DimensionLockType[] {
        let dimCount = this.getDimensionsCount(legend);
        let lockedDimensions =  new Array<DimensionLockType>(dimCount);
        for (let i = 0; i < legend.length; ++i) {
            for (let j = 0; j < legend[i].length; ++j) {
                lockedDimensions[legend[i][j].index] = legend[i][j].lockType;
            }
        }
        return lockedDimensions;
    }

    private recalcDepthSliderConstraints() {
        this.allowedMaxDepth = this.calcAllowedMaxDepth();
        if (this.allowedMaxDepth > this.maxPossibleDepth) {
            this.allowedMaxDepth = this.maxPossibleDepth
        }
        if (this.selectedMaxDepth > this.allowedMaxDepth) {
            this.selectedMaxDepth = this.allowedMaxDepth
        }
        let lockedDimsCount = this.getLockedDimensions(this.legend).filter(d => d == DimensionLockType.Value).length;
        if (this.selectedMinDepth < lockedDimsCount) {
            this.selectedMinDepth = lockedDimsCount;
        }
        this.allowedMinDepth = lockedDimsCount;
        if (this.selectedMinDepth < this.allowedMinDepth) {
            this.selectedMinDepth = this.allowedMinDepth;
        }
        if (this.selectedMaxDepth < this.selectedMinDepth) {
            this.selectedMaxDepth = this.selectedMinDepth;
        }
    }

    private calcAllowedMaxDepth(): number {
        let res = this.legend.length;
        for (let i = 0; i < this.legend.length; ++i) {
            if (this.legend[i][this.legend[i].length - 1].isSingleValue) {
                --res;
            }
        }
        return res;
    }

    public depthSliderValueChanged() {
        if (this.selectedMinDepth != this.savedMinDepth || this.selectedMaxDepth != this.savedMaxDepth) {
            this.setDepthFiltersDirty(true);
        }
    }

    public async explodeCard(i: number) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/ExplodeCard', {
                viewId: this.cardsModel.viewId,
                cardIndex: i,
                tabId: this.cardsModel.selectedTabId,
            });
            this.loadModel(model);
            let selectedTab = model.tabs.find(t => t.id == model.selectedTabId);
            this.routeToTabUId(selectedTab.tabUId);
        } finally {
            this.loading = false;
        }
    }

    public async removeCard(i: number) {
        this.removeCards([i]);
    }

    private async removeCards(cardIndexes: number[]) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/RemoveCards', {
                viewId: this.cardsModel.viewId,
                cardIndexes: cardIndexes,
                tabId: this.cardsModel.selectedTabId,
            });
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public addCardToParent(i: number) {
        return this.addCardsToParent([i]);
    }

    private async addCardsToParent(cardIndexes: number[]) {
        if (this.loading) {
            return;
        }
        if (!this.cardsModel.selectedTabId) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/AddCardsToParent', {
                viewId: this.cardsModel.viewId,
                cardIndexes: cardIndexes,
                tabId: this.cardsModel.selectedTabId,
            });
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public closeTab(tabId: string): void {
        function findTabIdsRecursive(list: string[], allTabs: Tab[], tabId: string) {
            for (let tab of allTabs.filter(t => t.parentTabId === tabId))
            {
                list.push(tab.id);
                findTabIdsRecursive(list, allTabs, tab.id);
            }
        }
        let tabsToClose = [ tabId ];
        findTabIdsRecursive(tabsToClose, this.cardsModel.tabs, tabId);
        if (tabsToClose.indexOf(this.cardsModel.selectedTabId) >= 0) {
            this.selectMainTab(this.cardsModel);
        }
        this.cardsModel.tabs = this.cardsModel.tabs.filter(t => tabsToClose.indexOf(t.id) < 0);

        this.dataService.post<CardsModel>('Processing/CloseTab', {
            viewId: this.cardsModel.viewId,
            tabId: tabId,
        });
        this.updateUIWithoutModelReload();
        this.reapplyFixWidthForScroll();
    }

    public enableDimensionValueSelectionForTabDimFilter(dimension : DimensionValue) {
        this.dimensionsFilterEditMode = true;
        this.selectedDimensionName = dimension;
        this.selectingDimensionForItemIndexed = undefined;
        this.selectingDimValueForTabDimIndex = dimension.index;
        this.dimensionValuesFilter = dimension.value;
        this.loadUniqueValuesForDimension(dimension.index);
        this.applyDimensionValuesFilterInternal();
        this.displayService.scrollToClass(this.ngZone, 'search-results', 500);
    }

    public async explodeBalanceCard() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/ExplodeBalanceCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
            });
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public algoIconClick(algo: DriverSelectionAlgorithm) {
        if (!this.showAlgoSelection) {
            this.showAlgoSelection = true;
            return;
        }
        this.selectedAlgorithm = algo;
    }

    public async saveAlgoSettings() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let model = await this.dataService.post<CardsModel>('Processing/ApplyAlgorithmSettings', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                algorithm: this.selectedAlgorithm,
                minDriverValue: this.minDriverValue,
            });
            this.loadModel(model);
        } finally {
            this.loading = false;
        }
    }

    public async cancelAlgoSettings () {
        this.selectedAlgorithm = this.savedAlgorithm;
        this.minDriverValue = this.savedMinDriverValue;
        this.showAlgoSelection = false;
    }

    public async createSubTotal (i: number) {
        if (this.loading || this.varianceModel.cards[i].subtotal || (i >= this.varianceModel.cards.length - 1 && !this.varianceModel.balanceCard)) {
            return;
        }
        let val = this.varianceModel.oldTotalOutcome;
        for (let j = 0; j <= i; ++j) {
            val += this.varianceModel.cards[j].seqDriverValue;
        }
        this.varianceModel.cards[i].subtotal = {
            name: 'Subtotal',
            value: val,
            isEditing: false,
        };
        await this.dataService.post<CardsModel>('Processing/CreateSubTotal', {
            viewId: this.cardsModel.viewId,
            tabId: this.cardsModel.selectedTabId,
            cardIndex: i,
        });
        this.updateUIWithoutModelReload();
    }

    private updateUIWithoutModelReload() {
        this.cardsModel.redoAvailable = false;
        this.cardsModel.undoAvailable = true;
    }

    public async renameSubtotal (i: number) {
        this.varianceModel.cards[i].subtotal.isEditing = false;
        this.dataService.post('Processing/RenameSubtotal', {
            viewId: this.cardsModel.viewId,
            tabId: this.cardsModel.selectedTabId,
            cardIndex: i,
            newName: this.varianceModel.cards[i].subtotal.name,
        });
        this.updateUIWithoutModelReload();
    }

    public removeSubtotal (i: number) {
        this.varianceModel.cards[i].subtotal = undefined;
        this.updateUIWithoutModelReload();
        this.dataService.post('Processing/RemoveSubtotal', {
            viewId: this.cardsModel.viewId,
            tabId: this.cardsModel.selectedTabId,
            cardIndex: i,
        });
    }

    public toggleShowOutOfScope(show: boolean) {
        this.showOutOfScope = show;
        this.loadWaterfallItems(this.varianceModel.cards, this.varianceModel.outOfScopeCards, this.varianceModel.balanceCard);
    }

    public toggleSidebarView(view: SidebarView) {
        if (this.sidebarView == view) {
            this.sidebarView = SidebarView.Hide;
        } else {
            this.sidebarView = view;
        }
        this.reapplyFixWidthForScroll();
    }

    public hideSidebar() {
        this.sidebarView = SidebarView.Hide;
        this.reapplyFixWidthForScroll();
    }

    private enableAbortedState(self: CardsComponent, error: string) {
        self.aborted = true;
        self.error = error;
        self.loadingBackground = false;
        self.stopRetries();
    }

    private stopRetries() {
        clearTimeout(this.retryTimeoutId);
    }

    public async abort() {
        try {
            this.stopRetries();
            await this.projectService.abortProcessing(this.projectId);
            this.enableAbortedState(this, null);
        } finally {
            this.loading = this.loadingBackground = false;
        }
    }

    public reload() {
        this.loadingBackground = true;
        this.aborted = false;
        this.retryCounter = 0;
        this.loadingBackgroundPercent = 0;
        this.loadingBackgroundCaption = "Processing...";
        this.loadCardsFromServer();
    }

    private createOnProgressUpdateHandler() {
        let self = this;
        return function(dto: ProcessingProgressDto) {
            if (!self.loadingBackground) {
                console.warn('Hit createOnProgressUpdateHandler without loadingBackground');
                console.log(dto);
                return;
            }
            if (dto.aborted) {
                console.log('aborted');
                self.enableAbortedState(self, dto.error);
            }
            self.loadProcessingProgress(dto, self);
            if (self.loadingBackgroundPercent >= 100) {
                self.loadingBackground = false;
                self.loading = true;
                self.loadCardsFromServer();
            }
        }
    }

    private loadProcessingProgress(dto: ProcessingProgressDto, self: CardsComponent) {
        if (!dto || dto.projectId != self.projectId) {
            return;
        }
        let percent: number = dto.percent;
            if (percent && (!self.loadingBackgroundPercent || self.loadingBackgroundPercent < percent)) {
                self.loadingBackgroundPercent = Math.round(percent);
                if (percent >= 100) {
                    self.loadCardsFromServer();
                }
            }
            let caption: string = dto.caption;
            if (caption) {
                self.loadingBackgroundCaption = caption;
            }
    }

    public cardMouseEnter(card: Card) {
        card.showAltView = true;
    }
    public cardMouseLeave(card: Card) {
        if (!card.diagramComboOpened) {
            card.showAltView = false;
        }
    }

    public opCh(openState: boolean, card: Card) {
        card.diagramComboOpened = openState;
    }

    public selectSeries(ser: BenchmarkDiagramSeries) {
        for (let i = 0; i < this.varianceModel.cards.length; ++i) {
            let card = this.varianceModel.cards[i];
            card.selectedDiagram = (this.seqMode ? card.altViewSeq.diagramSeries : card.altViewTot.diagramSeries).find(d => d.name == ser.name);
            card.diagramComboOpened = false;
        }
        if (this.cardsModel.selectedDiagramSeriesName === ser.name) {
            return;
        }
        this.cardsModel.selectedDiagramSeriesName = ser.name;
        this.updateUIWithoutModelReload();
        this.dataService.post<CardsModel>('Processing/SelectSeries', {
            viewId: this.cardsModel.viewId,
            seriesName: ser.name,
        });
    }

    public async removeDimensionFromCard(cardIx: number, dimIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/RemoveDimensionFromCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
                dimIndex: dimIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async addSingleDimensionCard(cardIx: number, dimIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/AddSingleDimensionCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
                dimIndex: dimIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async addNoDimensionsCard(cardIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/AddNoDimensionsCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async replaceCardWithOutOfScopeCard(cardIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/ReplaceCardWithOutOfScopeCard', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public async addCardAfterTab(cardIx: number) {
        try {
            this.loading = true;
            let cardsModel = await this.dataService.post<CardsModel>('Processing/AddCardAfterTab', {
                viewId: this.cardsModel.viewId,
                tabId: this.cardsModel.selectedTabId,
                cardIndex: cardIx,
            });
            this.loadModel(cardsModel);
        } finally {
            this.loading = false;
        }
    }

    public selectView(view: CardsModel) {
        for (let view of this.viewsModel.views) {
            view.isSelected = false;
            view.mode = ViewMode.normal;
        }
        view.isSelected = true;
        this.cardsModel = view;
        this.sortViewsBySelection();
    }

    private findViewByTabUId(tabUId: string): CardsModel {
        for (let i = 0; i < this.viewsModel.views.length; ++i) {
            if (this.viewsModel.views[i].tabUId == tabUId) {
                return this.viewsModel.views[i];
            }
            for (let j = 0; j < this.viewsModel.views[i].tabs.length; ++j) {
                if (this.viewsModel.views[i].tabs[j].tabUId == tabUId) {
                    return this.viewsModel.views[i];
                }
            }
        }
        return undefined;
    }

    private sortViewsBySelection() {
        this.viewsModel.views.sort((a,b) => {
            if (a.isSelected) {
                return 1;
            }
            if (b.isSelected) {
                return -1;
            }
            return +b.viewId - (+a.viewId);
        });
    }

    public enableEditMode(view: CardsModel) {
        if (this.copiedTab) {
            return;
        }
        for (let view of this.viewsModel.views) {
            view.mode = ViewMode.normal;
        }
        view.mode = ViewMode.edit;
    }

    public async duplicateView(view: CardsModel) {
        try {
            this.loading = true;

            let views = await this.dataService.post<ViewsModel>('processing/duplicateView', {
                viewId: view.viewId,
            });
            this.loadViews(views, undefined);
        } finally {
            this.loading = false;
        }
    }

    public deleteView(view: CardsModel) {
        this.viewsModel.views = this.viewsModel.views.filter(v => v.viewId != view.viewId);
        if (view.isSelected) {
            this.selectView(this.viewsModel.views[this.viewsModel.views.length - 1]);
        }
        this.dataService.post<ViewsModel>('processing/deleteView', {
            viewId: view.viewId,
        });
        this.reapplyFixWidthForScroll();
    }

    public renameView(view: CardsModel) {
        view.mode = ViewMode.normal;
        this.dataService.post('processing/renameView', {
            viewId: view.viewId,
            name: view.name,
        })
    }

    public async selectTabForCopying(view: CardsModel, tab: Tab) {
        if (this.copiedTab && this.copiedTab.viewId == view.viewId && this.copiedTab.tabId == tab.id) {
            this.unselectTabForCopying();
            return;
        }
        let tabId = tab ? tab.id : undefined;
        this.copiedTab = {
            viewId: view.viewId,
            tabId: tabId,
        };
        let compatibleTabs = await this.dataService.get<TabAddress[]>(`processing/GetCompatibleTabsForCopying?viewId=${view.viewId}&tabId=${tabId}`);
        this.markCompatibleTabs(compatibleTabs);
    }

    private unselectTabForCopying() {
        this.copiedTab = undefined;
        for (let view of this.viewsModel.views) {
            view.mainTabIsMarkedCompatible = false;
            for (let tab of view.tabs) {
                tab.isMarkedCompatible = false;
            }
        }
    }

    private markCompatibleTabs(tabAddresses: TabAddress[]) {
        function tabIsCompatible(viewId: string, tabId: string, tabAddresses: TabAddress[]) {
            return !!tabAddresses.find(ta => ta.viewId == viewId && ta.tabId == tabId);
        }
        for (let view of this.viewsModel.views) {
            view.mainTabIsMarkedCompatible = tabIsCompatible(view.viewId, null, tabAddresses);
            for (let tab of view.tabs) {
                tab.isMarkedCompatible = tabIsCompatible(view.viewId, tab.id, tabAddresses);
            }
        }
        this.selectedTabIsCompatible = tabIsCompatible(this.cardsModel.viewId, this.cardsModel.selectedTabId, tabAddresses);
    }

    public async pasteCopiedTab(cardIx: number) {
        try {
            this.loading = true;
            let updatedTargetView = await this.dataService.post<CardsModel>('processing/pasteCopiedTab', {
                sourceViewId: this.copiedTab.viewId,
                sourceTabId: this.copiedTab.tabId,
                targetViewId: this.cardsModel.viewId,
                targetTabId: this.cardsModel.selectedTabId,
                targetCardIx: cardIx,
            });
            this.loadModel(updatedTargetView);
        } finally {
            this.loading = false;
        }
    }

    public onClickedOutsideTabs($event) {
        this.unselectTabForCopying();
    }

    public markForBulkRemoveClick(card: Card) {
        if (card.markedForBulkRemove) {
            card.markedForBulkRemove = false;
            if (!this.anyCardIsMarkedForBulkRemove()) {
                this.multiSelectMode = CardsMultiSelectMode.Undecided;
            }
        } else if (!this.anyCardIsMarkedForBulkAddToParent() && !this.anyCardIsMarkedForBulkRequestStory()) {
            card.markedForBulkRemove = true;
            this.multiSelectMode = CardsMultiSelectMode.Remove;
        }
    }

    public markForBulkRequestStoryClick(card: Card) {
        if (card.markedForBulkRequestStory) {
            card.markedForBulkRequestStory = false;
            if (!this.anyCardIsMarkedForBulkRequestStory()) {
                this.multiSelectMode = CardsMultiSelectMode.Undecided;
            }
        } else if (!this.anyCardIsMarkedForBulkRemove() && !this.anyCardIsMarkedForBulkAddToParent()) {
            card.markedForBulkRequestStory = true;
            this.multiSelectMode = CardsMultiSelectMode.RequestStory;
        }
    }

    public markForBulkAddToParentClick(card: Card) {
        if (card.markedForBulkAddToParent) {
            card.markedForBulkAddToParent = false;
            if (!this.anyCardIsMarkedForBulkAddToParent()) {
                this.multiSelectMode = CardsMultiSelectMode.Undecided;
            }
        } else if (!this.anyCardIsMarkedForBulkRemove() && !this.anyCardIsMarkedForBulkRequestStory()) {
            card.markedForBulkAddToParent = true;
            this.multiSelectMode = CardsMultiSelectMode.AddToParent;
        }
    }

    private anyCardIsMarkedForBulkRemove() {
        return !!this.varianceModel.cards.find(c => c.markedForBulkRemove);
    }

    private anyCardIsMarkedForBulkAddToParent() {
        return !!this.varianceModel.cards.find(c => c.markedForBulkAddToParent);
    }

    private anyCardIsMarkedForBulkRequestStory() {
        return !!this.varianceModel.cards.find(c => c.markedForBulkRequestStory);
    }

    public toggleMultiSelect() {
        if (this.multiSelectMode === CardsMultiSelectMode.None) {
            this.multiSelectMode = CardsMultiSelectMode.Undecided;
        } else {
            this.multiSelectMode = CardsMultiSelectMode.None;
        }
    }

    public turnOffMultiSelect() {
        this.multiSelectMode = CardsMultiSelectMode.None;
        if (this.varianceModel && this.varianceModel.cards) {
            for (let card of this.varianceModel.cards) {
                card.markedForBulkAddToParent = false;
                card.markedForBulkRemove = false;
                card.markedForBulkRequestStory = false;
            }
        }
    }

    public async applyBulkOperation() {
        if (this.anyCardIsMarkedForBulkAddToParent()) {
            let indexes = [];
            for (let i = 0; i < this.varianceModel.cards.length; ++i) {
                if (this.varianceModel.cards[i].markedForBulkAddToParent) {
                    indexes.push(i);
                }
            }
            await this.addCardsToParent(indexes);
        } else if (this.anyCardIsMarkedForBulkRemove()) {
            let indexes = [];
            for (let i = 0; i < this.varianceModel.cards.length; ++i) {
                if (this.varianceModel.cards[i].markedForBulkRemove) {
                    indexes.push(i);
                }
            }
            await this.removeCards(indexes);
        } else if (this.anyCardIsMarkedForBulkRequestStory()) {
            let indexes = [];
            for (let i = 0; i < this.varianceModel.cards.length; ++i) {
                if (this.varianceModel.cards[i].markedForBulkRequestStory) {
                    indexes.push(i);
                }
            }
            this.turnOffMultiSelect();
            await this.loadStory(this.getSelectedTabUId(), indexes);
        } else {
            this.turnOffMultiSelect();
        }
    }

    public noCardsDownloadLinkClick() {
        this.notifier.showInfo('No cards in scope');
    }

    private loadStories() {
        for (let i = 0; i < this.viewsModel.views.length; ++i) {
            this.loadStory(this.viewsModel.views[i].tabUId);
            if (this.viewsModel.views[i].tabs){
                for (let j = 0; j < this.viewsModel.views[i].tabs.length; ++j) {
                    this.loadStory(this.viewsModel.views[i].tabs[j].tabUId);
                }
            }
        }
    }

    private getStoryContainer(view: CardsModel, tab: Tab | undefined) : [IHasStory, boolean] {
        let storyContainer: IHasStory;
        let isSelected: boolean;
        if (tab) {
            if (this.cardsModel && this.cardsModel.viewId == view.viewId && this.cardsModel.selectedTabId == tab.id) {
                isSelected = true;
            }
            storyContainer = tab;
        } else {
            if (this.cardsModel && this.cardsModel.viewId == view.viewId && this.cardsModel.selectedTabId === undefined) {
                isSelected = true;
            }
            storyContainer = view;
        }
        return [ storyContainer, isSelected];
    }

    private loadStory(tabUId: string, ixs: number[] = undefined) {
        let [ view, tab ] = CardHelper.findByTabUId(this.viewsModel.views, tabUId);
        if (!view ) {
            console.error("Couldn't find a view or a tab with tabUId " + tabUId);
            return;
        }

        let [ storyContainer, isSelected ] = this.getStoryContainer(view, tab);
        storyContainer.loadingStory = true;
        if (isSelected) {
             this.loadingStory = true;
        }

        let tz = this.getTimeZoneOffset();
        let url: string;
        if (ixs) {  //manually called
            url = `processing/generateStory/${view.viewId}?ixs=${ixs.join(',')}&tz=${tz}`;
            if (tab) {
                url += `&tabId=${tab.id}`;
            }
        } else {
            url = `processing/getStory/${this.projectId}?tabUId=${tabUId}&tz=${tz}`;
        }
        this.dataService.get<any>(url).then(res => {
            [ storyContainer, isSelected ] = this.getStoryContainer(view, tab); //search again, cause another tab could've been selected meanwhile
            let newStory: string;
            if (ixs) {
                if (isSelected) {
                    newStory = this.story ? res.story + '<br>' + this.story : res.story;
                } else {
                    newStory = storyContainer.story ? res.story + '<br>' + storyContainer.story : res.story;
                }
            } else {
                newStory = res.story;
            }

            storyContainer.story = newStory;
            storyContainer.loadingStory = false;
            if (isSelected) {
                this.story = newStory;
                this.loadingStory = false;
                this.unfocusQuill();    //will be sent to the server on blur
            }
        }).catch(err => {
            console.error('Story loading error:');
            console.error(err);
        });
    }


    private isInternalNgRoute(url: string): boolean {
        const route = url.split('?')[0].substring(1);
        return this.router.config.some(r => route === r.path);
    }

    private quillInstance: any;
    isHoveredQuill: boolean = false;
    public quillBlur($event) {
        if(!this.isHoveredQuill) $event.editor.theme.modules.toolbar.container.style.display = "none";
        if (this.story != this.savedStory) {
            this.saveStory();
        }
    }

    public quillClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        let classes = target.getAttribute('class');
        if (target.tagName === 'A' && target.hasAttribute('href') && classes == 'ql-preview') {
            event.preventDefault();

            const href = target.getAttribute('href');
            console.log('href=' + href);

            const url = decodeURIComponent(href);
            console.log('url=' + url);

            if (this.isInternalNgRoute(url)) {
                this.router.navigateByUrl(href);
            } else {
                window.open(url, '_blank');
            }
        }

        //console.log('WYSIWYG story: ' + this.story);
    }

    public showToolbar() {
        if (this.quillInstance && this.quillInstance.getModule('toolbar')) {
          const toolbar = this.quillInstance.getModule('toolbar').container;
          toolbar.style.display = 'block';
          this.isHoveredQuill = true;
        }
      }

    public hideToolbar() {
        this.isHoveredQuill = false;
        if (this.quillInstance && !this.quillInstance.hasFocus() && this.quillInstance.getModule('toolbar')) {
          const toolbar = this.quillInstance.getModule('toolbar').container;
          toolbar.style.display = 'none';
        }
      }

    public onQuillCreated($event) {
        this.quillInstance = $event;
    }

    private unfocusQuill() {
        if (this.quillInstance) {
            this.quillInstance.blur();
        }
    }

    private saveStory() {
        let tabUId: string;
        let selectedTab = this.getSelectedTab();
        if (selectedTab) {
            tabUId = selectedTab.tabUId;
            selectedTab.story = this.story;
        } else {
            tabUId = this.cardsModel.tabUId;
            this.cardsModel.story = this.story;
        }
        this.savedStory = this.story;
        this.dataService.post('processing/saveStory', {
            projectId: this.projectId,
            tabUId: tabUId,
            story: this.story,
        });
    }

    private getSelectedTabUId(): string {
        let selectedTab = this.getSelectedTab();
        if (selectedTab) {
            return selectedTab.tabUId;
        } else {
            return this.cardsModel.tabUId;
        }
    }

    public removeForecastAdjustment(){
        this.notifier.showError('Not implemented');
    }

    public selectFcst(val: boolean) {
        this.showFcst = val;
        if (this.cardsModel.selectedTabId) {
            this.selectTabById(this.cardsModel.viewId, this.cardsModel.selectedTabId);
        } else {
            this.selectMainTab(this.cardsModel);
        }
    }

    private getTimeZoneOffset() {
        return -new Date().getTimezoneOffset();
    }

    private getSelectedViewKey(projectId: string): string {
        return 'selectedView_' + projectId;
    }

    private saveSelectedViewId(viewId: string): void {
        localStorage.setItem(this.getSelectedViewKey(this.projectId), viewId);
        console.log('saveSelectedViewId ' + viewId);
    }

    private getSavedSelectedViewId() {
        return localStorage.getItem(this.getSelectedViewKey(this.projectId));
    }
}